import { Controller } from "@hotwired/stimulus"

// Boutons de la barre d'outils Jitsi autorises
const TOOLBAR_BUTTONS = [
  "microphone", "camera", "desktop",
  "raisehand", "participants-pane", "tileview",
  "whiteboard", "select-background",
  "hangup", "fullscreen"
]

// Nombre max de flux video recus simultanement (les autres tuiles = avatar).
// Borne le decodage CPU et la bande passante descendante de chaque participant,
// independamment de la taille de la promotion. 12 = grille 3x4 confortable.
const CHANNEL_LAST_N = 12

// Contraintes de capture video differenciees par role : le formateur (moderator)
// presente en HD, les eleves capturent en resolution reduite (leur uplink est le
// maillon faible et leur vignette n'a pas besoin de plus).
const VIDEO_CONSTRAINTS = {
  moderator: { video: { height: { ideal: 720, max: 720, min: 180 } } },
  participant: { video: { height: { ideal: 360, max: 480, min: 180 } } }
}

export default class extends Controller {
  static targets = ["container", "placeholder"]
  static values = {
    room: String,
    userName: String,
    domain: String, // Domaine Jitsi (ex: "meet.dalms.fr" ou "meet.lvh.me:8443")
    jwt: { type: String, default: "" }, // Token JWT pour l'authentification
    moderator: { type: Boolean, default: false }, // Formateur = camera active + HD, eleve = camera coupee + basse def
    status: { type: String, default: "" }, // Gating : les eleves ne rejoignent qu'en "live"
    // Textes UI (i18n via data-values, cf. convention CLAUDE.md)
    errorMessage: { type: String, default: "Impossible de charger la visioconférence." },
    retryLabel: { type: String, default: "Réessayer" },
    leftMessage: { type: String, default: "Vous avez quitté la session." },
    rejoinLabel: { type: String, default: "Rejoindre à nouveau" },
    endedMessage: { type: String, default: "La session est terminée." }
  }

  connect() {
    // Gating : le moderateur (formateur) peut preparer sa salle avant le demarrage ;
    // les eleves ne montent la visio qu'une fois la session "live" (pas de room sans
    // moderateur avant le Start). Les eleves en attente rejoignent via start().
    if (this.moderatorValue || this.statusValue === "live") {
      this.mount()
    }
  }

  disconnect() {
    this.disposeApi()
  }

  // --- Actions (declenchees par les broadcasts de live_session_controller) ---

  // Demarrage de session (broadcast session_started) : les eleves en attente rejoignent.
  start() {
    if (!this.api) this.mount()
  }

  // Fin de session cote formateur : couper proprement la visio de chaque participant.
  end() {
    this.disposeApi()
    this.renderNotice(this.endedMessageValue)
  }

  // --- Chargement du script Jitsi ---

  loadJitsiApi() {
    if (window.JitsiMeetExternalAPI) {
      this.initJitsi()
      return
    }

    const script = document.createElement("script")
    script.src = `https://${this.domainValue}/external_api.js`
    script.onload = () => this.initJitsi()
    script.onerror = () => {
      console.error("[LiveSessionJitsi] Impossible de charger le script Jitsi Meet")
      this.renderNotice(this.errorMessageValue, this.retryLabelValue)
    }
    document.head.appendChild(script)
  }

  // --- Initialisation de l'iframe Jitsi ---

  initJitsi() {
    if (!this.hasContainerTarget) {
      console.error("[LiveSessionJitsi] Container target manquant")
      return
    }

    if (!this.roomValue) {
      console.error("[LiveSessionJitsi] Room value manquant")
      return
    }

    if (!this.domainValue) {
      console.error("[LiveSessionJitsi] Domain value manquant")
      return
    }

    try {
      const options = {
        roomName: this.roomValue,
        parentNode: this.containerTarget,
        width: "100%",
        height: "100%",
        userInfo: {
          displayName: this.userNameValue || "Participant"
        },
        configOverwrite: {
          startWithAudioMuted: true,
          // Les eleves entrent camera coupee (evite le pic de N uplinks au demarrage,
          // camera activable a la demande) ; le formateur entre camera active.
          startWithVideoMuted: !this.moderatorValue,
          // Plafonne le nombre de flux recus et la resolution de capture pour tenir
          // la charge sur des promotions de 10-30 participants.
          channelLastN: CHANNEL_LAST_N,
          constraints: this.moderatorValue ? VIDEO_CONSTRAINTS.moderator : VIDEO_CONSTRAINTS.participant,
          toolbarButtons: TOOLBAR_BUTTONS,
          disableDeepLinking: true,
          prejoinPageEnabled: false,
          disableLocalStorage: true,
          analytics: { disabled: true },
          enableUserRolesBasedOnToken: true,
          readOnlyName: true
        },
        interfaceConfigOverwrite: {
          SHOW_JITSI_WATERMARK: false,
          SHOW_WATERMARK_FOR_GUESTS: false,
          SHOW_BRAND_WATERMARK: false,
          SHOW_POWERED_BY: false,
          DEFAULT_BACKGROUND: "#1e293b"
        }
      }

      // Ajouter le JWT si present (authentification Jitsi)
      if (this.jwtValue) {
        options.jwt = this.jwtValue
      }

      this.api = new window.JitsiMeetExternalAPI(this.domainValue, options)
      this.registerApiListeners()
      console.log("[LiveSessionJitsi] Jitsi Meet initialise pour la room:", this.roomValue)
    } catch (error) {
      console.error("[LiveSessionJitsi] Erreur initialisation Jitsi:", error)
      this.renderNotice(this.errorMessageValue, this.retryLabelValue)
    }
  }

  // --- Cycle de vie de la conference ---

  registerApiListeners() {
    // La conference est etablie : retirer le spinner de chargement.
    this.api.addListener("videoConferenceJoined", () => this.hidePlaceholder())
    // L'utilisateur a clique "quitter" : couper l'iframe et proposer un re-join.
    this.api.addListener("readyToClose", () => {
      this.disposeApi()
      this.renderNotice(this.leftMessageValue, this.rejoinLabelValue)
    })
  }

  disposeApi() {
    if (this.api) {
      this.api.dispose()
      this.api = null
    }
  }

  hidePlaceholder() {
    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.add("hidden")
    }
  }

  // Vide le conteneur et affiche un message ; si actionLabel est fourni, ajoute un
  // bouton qui relance la visio (re-join apres sortie, retry apres erreur).
  renderNotice(message, actionLabel = null) {
    if (!this.hasContainerTarget) return

    this.containerTarget.replaceChildren()

    const wrapper = document.createElement("div")
    wrapper.classList.add("flex", "flex-col", "items-center", "justify-center", "gap-3", "text-center", "p-6")

    const text = document.createElement("p")
    text.classList.add("text-sm", "text-slate-300")
    text.textContent = message
    wrapper.appendChild(text)

    if (actionLabel) {
      const button = document.createElement("button")
      button.type = "button"
      button.classList.add("student-btn", "student-btn-primary", "text-sm")
      button.textContent = actionLabel
      button.addEventListener("click", () => this.mount())
      wrapper.appendChild(button)
    }

    this.containerTarget.appendChild(wrapper)
  }

  // Monte (ou remonte) la visio depuis zero : vide le conteneur — placeholder d'attente,
  // spinner ou notice — puis (re)charge Jitsi. Utilise au connect, au start, au re-join
  // apres sortie et au retry apres echec de chargement.
  mount() {
    if (this.hasContainerTarget) {
      this.containerTarget.replaceChildren()
    }
    this.loadJitsiApi()
  }
}
